import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import TableField from "components/Table";
import { Loading } from "components/Loading";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import { v4 as uuidv4 } from "uuid";
import { getClientsColumns } from "./const/columns";
import { fetchClientsData } from "./helper";

const Clients = ({ auth, timeRange }) => {
  const theme = useTheme();
  const { dataMap, sendBatch } = useDataSourceManager();
  const [requestUuid] = useState(uuidv4());
  const [isLoading, setIsLoading] = useState(true);

  const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;
  const summaryReportId = timeRange?.summaryReportId;

  const [clientsData, setClientsData] = useState([]);
  const columnHelper = createColumnHelper();

  const { fromTime, toTime } = React.useMemo(() => {
    const weekStart = timeRange?.weekStart ? new Date(`${timeRange.weekStart}T00:00:00`) : null;
    const weekEnd = timeRange?.weekEnd ? new Date(`${timeRange.weekEnd}T23:59:59`) : null;
    return {
      fromTime: weekStart && !isNaN(weekStart.getTime()) ? Math.floor(weekStart.getTime() / 1000) : 0,
      toTime: weekEnd && !isNaN(weekEnd.getTime()) ? Math.floor(weekEnd.getTime() / 1000) : 0,
    };
  }, [timeRange]);

  const clientsTableInstance = useTableInstance({
    tableName: "summaryReportClientsTable",
    data: clientsData,
    enableSorting: true,
    enablePagination: true,
    initialState: {
      sorting: [{ id: "connectivity", desc: true }]
    },
    columns: getClientsColumns({
      columnHelper,
      fromTime,
      toTime,
    }),
  });

  const getClientsData = async () => {
    if (!summaryReportId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    try {
      const items = await fetchClientsData({
        summaryReportId,
        standalone,
        auth,
        requestUuid,
        sendBatch,
      });
      setClientsData(items);
    } catch (err) {
      console.error("Failed to load summary report clients data", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClientsData();
  }, [summaryReportId, requestUuid, standalone, auth.realm?.id, auth.orgs?.id, auth.sites?.id]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loading />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        px: "20px",
        pb: "30px",
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontSize: theme.typography.customFontSize[4],
          fontWeight: theme.typography.customFontWeight[3],
          color: theme.palette.primary.main,
          lineHeight: "24px",
          mb: "14px",
        }}
      >
        Client Connectivity
      </Typography>
      <TableField
        {...clientsTableInstance}
        pendingChildren={
          <Typography
            sx={{
              color: "#AAAAAA",
              py: 3,
              textAlign: "center",
            }}
          >
            No data available
          </Typography>
        }
      />
    </Box>
  );
};

export default Clients;
